import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import classNames from 'classnames'
import { isEmpty, isNil } from 'lodash'
import { IconArrowsMaximize, IconFileTypePdf, IconGripVertical, IconX } from '@tabler/icons-react'

import { Banner } from '@/components/banners/Banner'
import { Button } from '@/components/buttons/Button'
import { PageHeader } from '@/components/headers/PageHeader'
import { Pill } from '@/components/pills/Pill'
import { SavedFilters } from '@/components/savedFilters/SavedFilters'
import { COPILOT_FILTER_TYPE } from '@/reducers/savedFilters/savedFilters.constants'
import {
  setSavedFilterById,
  clearSavedFilter,
  fetchCopilotFilterData,
  loadDefaultFilters,
  fetchCopilotDashboardData,
} from '@/reducers/qa-copilot/qa-copilot.actions'
import { setFilter, setScopeOverlay } from '@/reducers/qa-copilot/qa-copilot.redux'
import { Drawer } from '@/components/drawer/Drawer'
import { usePrintToPdf } from '@/utils/hooks/usePrintToPdf'

import { CopilotFilters } from '../../components/CopilotFilters'
import {
  SECTIONS,
  WIDGET_BY_ID,
  widgetsForTab,
  isResizable,
  nextSizeBucket,
  closestBucketForSize,
} from './widgetRegistry'
import {
  buildSeedTab,
  filterVisibleEntries,
  loadState,
  moveEntryToTarget,
  saveState,
  setLayoutEntryVisibility,
} from './helpers/layout'
import { AddWidgetMenu } from './components/AddWidgetMenu'
import { ResetTabConfirm } from './components/ResetTabConfirm'
import { DashboardTabs } from './components/DashboardTabs'
import { ScoredCallsBrowser } from './drawers/ScoredCallsBrowser'
import { NavTrail } from './components/NavTrail'
import { OverviewCardsRow } from './widgets/OverviewCardsRow'
import {
  buildScorecardNameMap,
  getAgentLabel,
  getCriterionLabel,
  getScorecardLabel,
} from './drawers/entityDrawer.helpers'
import {
  BrowseCallsContext,
  EntityClickContext,
  PickEntityContext,
  PickedEntityValuesContext,
} from './helpers/dashboardContexts'
import { AgentDetailDrawer } from './drawers/AgentDetailDrawer'
import { CriterionDetailDrawer } from './drawers/CriterionDetailDrawer'
import { ScorecardDetailDrawer } from './drawers/ScorecardDetailDrawer'
import {
  SectionAgentPicker,
  SectionScorecardPicker,
  SectionTagPicker,
} from './components/SectionEntityPicker'
import { ViewAsAgentBanner } from './components/ViewAsAgentBanner'
import { buildViewAsAgentFilterValue, isViewAsAgentFilter } from './helpers/format'
import { useServerLayoutSync } from './hooks/useServerLayoutSync'

import '../copilotDashboard.scss'
import './customizableDashboard.scss'

const TAB_FILTER_KEY = {
  scorecard: 'scorecards',
  agent: 'agents',
  tag: 'tags',
}

const ENTITY_NOUN_BY_TAB = {
  scorecard: 'scorecard',
  agent: 'agent',
  tag: 'tag',
}

// The At-a-glance KPI band renders as a fixed full-width header above
// the bento grid rather than as a grid cell: its tile count wraps with
// viewport width, and a fixed-height grid cell can't grow to fit the
// wrapped rows (they clipped, then overlapped the row below). So it's
// pulled out of the draggable/hideable layout entirely.
const OVERVIEW_WIDGET_ID = 'overview_cards'

// Everything drags anywhere: one flat grid, no band fences. (The KPI
// bar still moves as one unit -- its 5 tiles are a single widget.)
const sameDragGroup = (aId, bId) => !!WIDGET_BY_ID[aId] && !!WIDGET_BY_ID[bId]

// Tabs that wire the picker into a per-entity cohort comparison. Every
// non-overview tab has at least the pass-rate-vs-cohort + score-
// trend-vs-cohort pair backing the banner's "Comparing X vs the rest"
// framing.
const COHORT_BANNER_TABS = new Set(['scorecard', 'agent', 'tag'])

// Only the bento cells' charts belong in the export.
const PRINT_CHART_SELECTOR = '.bento-cell div[_echarts_instance_]'

export const CustomizableDashboardPage = () => {
  const dispatch = useDispatch()
  const { data } = useSelector((reduxState) => reduxState.qaCopilot)
  const { selectedSavedFilterId } = data
  const {
    user_id: userId,
    organizationid: currentUserOrgId,
    own_organization_id: ownOrganizationId,
    hierarchy_manager: isHierarchyManager,
  } = useSelector((reduxState) => reduxState.currentUser)
  const childOrgSelected = isHierarchyManager && currentUserOrgId !== ownOrganizationId

  // ---- State ----
  const [state, setState] = useState(() => loadState(userId))
  const [selectedAgent, setSelectedAgent] = useState(null)
  const [selectedCriterion, setSelectedCriterion] = useState(null)
  const [selectedScorecard, setSelectedScorecard] = useState(null)
  // null = closed; object (possibly empty) = open with scope overrides
  // that merge over the current dashboard filters.
  const [browseOverrides, setBrowseOverrides] = useState(null)
  const browseCallsOpen = !isNil(browseOverrides)
  const [trail, setTrail] = useState([])

  const pushTrail = useCallback((entry) => {
    setTrail((prev) => [...prev, entry])
  }, [])
  // Drawers stack (criterion -> scorecard cross-link), so closing one
  // must remove ITS breadcrumb, not blindly the last entry -- popping
  // the tail from a lower drawer desyncs the trail from drawer state.
  const popTrail = useCallback((type) => {
    setTrail((prev) => {
      if (isEmpty(prev)) {
        return prev
      }
      if (!type) {
        return prev.slice(0, -1)
      }
      const idx = prev.map((entry) => entry.type).lastIndexOf(type)
      if (idx === -1) {
        return prev
      }
      return [...prev.slice(0, idx), ...prev.slice(idx + 1)]
    })
  }, [])
  // index === -1 jumps to the dashboard root; closes every drilldown.
  const handleTrailJump = useCallback((index) => {
    setTrail((prev) => {
      prev.slice(index + 1).forEach((entry) => {
        if (entry.type === 'agent') {
          setSelectedAgent(null)
        } else if (entry.type === 'criterion') {
          setSelectedCriterion(null)
        } else if (entry.type === 'scorecard') {
          setSelectedScorecard(null)
        } else if (entry.type === 'browse-calls') {
          setBrowseOverrides(null)
        }
      })
      return prev.slice(0, index + 1)
    })
  }, [])

  const openBrowseCalls = useCallback(
    (overrides) => {
      setBrowseOverrides(overrides || {})
      const tier = overrides?.initialTier
      const label =
        tier === 'below' ? 'Below threshold' : tier === 'above' ? 'Above threshold' : 'All calls'
      pushTrail({ type: 'browse-calls', label, payload: overrides })
    },
    [pushTrail]
  )
  const browseCallsContextValue = useMemo(() => ({ openBrowseCalls }), [openBrowseCalls])

  // Fallback name lookup for scorecard payloads that only carry a sid.
  // Prevents the breadcrumb from showing a stale org/company name that
  // ended up in a widget's payload (see getScorecardLabel).
  const scorecardNameById = useMemo(() => buildScorecardNameMap(data.scorecards), [data.scorecards])

  const handleEntityClick = useCallback(
    ({ type, payload }) => {
      if (type === 'agent') {
        setSelectedAgent(payload)
        pushTrail({ type: 'agent', label: getAgentLabel(payload), payload })
      }
      if (type === 'criterion') {
        setSelectedCriterion(payload)
        pushTrail({ type: 'criterion', label: getCriterionLabel(payload), payload })
      }
      if (type === 'scorecard') {
        setSelectedScorecard(payload)
        pushTrail({
          type: 'scorecard',
          label: getScorecardLabel(payload, scorecardNameById),
          payload,
        })
      }
    },
    [pushTrail, scorecardNameById]
  )

  const { activeTab } = state
  const activeTabState = state.tabs[activeTab] || state.tabs.overview

  const filterAgents = useSelector((reduxState) => reduxState.qaCopilot.filters?.agents)
  const viewAsAgent = useMemo(
    () => (isViewAsAgentFilter(filterAgents) ? filterAgents[0] : null),
    [filterAgents]
  )
  const inViewAs = !!viewAsAgent

  // Respect the filter bar's selected org (Balto admin / hierarchy
  // manager viewing another org) -- refetching with the bare user org
  // here silently flipped view-as data back to the admin's own org.
  const viewAsOrgId = useSelector(
    (reduxState) => reduxState.qaCopilot.filters?.organizationId || currentUserOrgId
  )
  const enterViewAs = useCallback(
    (agent) => {
      dispatch(setFilter({ agents: [buildViewAsAgentFilterValue(agent)] }))
      dispatch(fetchCopilotDashboardData(viewAsOrgId))
      // The agent drawer closes here -- pop ITS breadcrumb too, or the
      // crumb outlives the drawer (and repeat view-as entries stack
      // orphaned AGENT crumbs the banner's Clear can't remove).
      setSelectedAgent(null)
      popTrail('agent')
    },
    [dispatch, viewAsOrgId, popTrail]
  )

  const clearViewAs = useCallback(() => {
    dispatch(setFilter({ agents: [] }))
    dispatch(fetchCopilotDashboardData(viewAsOrgId))
  }, [dispatch, viewAsOrgId])

  // Init pair only fires once per page lifetime.
  useEffect(() => {
    dispatch(loadDefaultFilters())
    dispatch(fetchCopilotFilterData(currentUserOrgId))
  }, [])

  useEffect(() => {
    saveState(userId, state)
  }, [state, userId])

  // The scope overlay and the view-as agent filter are THIS page's
  // modes, but they live in redux -- without cleanup they leak into
  // the classic dashboard's fetches after navigating away (classic
  // silently renders entity-scoped data as if it were org-wide).
  const inViewAsRef = useRef(inViewAs)
  inViewAsRef.current = inViewAs
  useEffect(
    () => () => {
      dispatch(setScopeOverlay(null))
      if (inViewAsRef.current) {
        dispatch(setFilter({ agents: [] }))
      }
    },
    [dispatch]
  )

  // Server-side persistence (dashboard_settings row per user).
  // localStorage above stays as the warm cache for first paint; the
  // server copy is the cross-browser source of truth.
  useServerLayoutSync(currentUserOrgId, state, setState)

  const handleDownloadPdf = usePrintToPdf(PRINT_CHART_SELECTOR)

  const setEntryVisibility = useCallback((widgetId, visible) => {
    setState((prev) => setLayoutEntryVisibility(prev, prev.activeTab, widgetId, visible))
  }, [])

  // Cycle the widget through the canonical size buckets. Writes the
  // new w/h into the stored entry (which renderWidgetCell prefers
  // over the registry default), so the choice persists per user.
  const cycleEntrySize = useCallback((widgetId, currentW, currentH) => {
    const bucket = nextSizeBucket(currentW, currentH, WIDGET_BY_ID[widgetId])
    setState((prev) => {
      const tab = prev.tabs[prev.activeTab]
      const layout = (tab.layout || []).map((entry) =>
        entry.id === widgetId
          ? { ...entry, layout: { ...entry.layout, w: bucket.size.w, h: bucket.size.h } }
          : entry
      )
      return { ...prev, tabs: { ...prev.tabs, [prev.activeTab]: { ...tab, layout } } }
    })
  }, [])

  // ---- Drag-to-reorder (native HTML5 DnD) ----
  // rbd can't do CSS grid (list-only), so cells use the platform drag
  // API from a grip handle. Drops are restricted to the same section +
  // band: array order only controls order WITHIN a band, so a cross-
  // band drop would reorder invisibly and read as a broken drag.
  const [dragId, setDragId] = useState(null)
  const [dragOverId, setDragOverId] = useState(null)
  const [justMovedId, setJustMovedId] = useState(null)

  const handleDrop = useCallback((droppedOnId) => {
    setDragId((currentDragId) => {
      if (currentDragId && sameDragGroup(currentDragId, droppedOnId)) {
        setState((prev) => {
          const tab = prev.tabs[prev.activeTab]
          let nextLayout = moveEntryToTarget(tab.layout, currentDragId, droppedOnId)
          if (nextLayout === tab.layout) {
            return prev
          }
          // Drop-to-adopt: when the sizes differ, the dragged widget
          // takes the drop target's slot size (if it has a bucket at
          // that width). Without this, dropping a full-width chart on
          // a half-width one reorders the array but the dense grid
          // re-flows to the exact same picture.
          const draggedWidget = WIDGET_BY_ID[currentDragId]
          const targetEntry = tab.layout.find((entry) => entry.id === droppedOnId)
          const targetWidget = WIDGET_BY_ID[droppedOnId]
          const targetW = targetEntry?.layout?.w || targetWidget?.defaultLayout?.w
          const targetH = targetEntry?.layout?.h || targetWidget?.defaultLayout?.h
          const draggedEntry = tab.layout.find((entry) => entry.id === currentDragId)
          const draggedW = draggedEntry?.layout?.w || draggedWidget?.defaultLayout?.w
          if (targetW && draggedW !== targetW && isResizable(draggedWidget)) {
            const bucket = closestBucketForSize(draggedWidget, targetW, targetH || 10)
            if (bucket) {
              nextLayout = nextLayout.map((entry) =>
                entry.id === currentDragId
                  ? { ...entry, layout: { ...entry.layout, w: bucket.size.w, h: bucket.size.h } }
                  : entry
              )
            }
          }
          // A drop means "place next to that widget", but the dense
          // grid re-flows around widget sizes -- a large chart dropped
          // near a small slot can land somewhere non-obvious. Flash +
          // scroll to the moved cell so the outcome is always visible.
          setJustMovedId(currentDragId)
          setTimeout(() => {
            document
              .querySelector(`[data-widget-id="${currentDragId}"]`)
              ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
          }, 50)
          setTimeout(() => setJustMovedId(null), 1600)
          return {
            ...prev,
            tabs: { ...prev.tabs, [prev.activeTab]: { ...tab, layout: nextLayout } },
          }
        })
      }
      return null
    })
    setDragOverId(null)
  }, [])

  const renderWidgetCell = (entry) => {
    const widget = WIDGET_BY_ID[entry.id]
    if (!widget) {
      return null
    }
    // Stored layout wins so per-user size choices persist; registry
    // defaultLayout is the fallback for entries seeded before the
    // size was stored (or after a registry change).
    const w = entry.layout?.w || widget.defaultLayout?.w || 6
    const h = entry.layout?.h || widget.defaultLayout?.h || 10
    const isValidDropTarget = dragId && dragId !== entry.id && sameDragGroup(dragId, entry.id)
    return (
      <div
        key={entry.id}
        data-widget-id={entry.id}
        className={classNames('bento-cell', {
          'is-drag-over': dragOverId === entry.id && isValidDropTarget,
          'is-just-moved': justMovedId === entry.id,
        })}
        style={{ gridColumn: `span ${w}`, gridRow: `span ${h}` }}
        onDragOver={(event) => {
          if (!isValidDropTarget) {
            return
          }
          event.preventDefault()
          event.dataTransfer.dropEffect = 'move'
          setDragOverId(entry.id)
        }}
        onDragLeave={(event) => {
          // Only clear when actually leaving the cell (not entering a child).
          if (!event.currentTarget.contains(event.relatedTarget)) {
            setDragOverId((prev) => (prev === entry.id ? null : prev))
          }
        }}
        onDrop={(event) => {
          event.preventDefault()
          handleDrop(entry.id)
        }}
      >
        <button
          type="button"
          className="bento-cell-drag-handle"
          draggable
          onDragStart={(event) => {
            event.dataTransfer.effectAllowed = 'move'
            // Drag image = the whole cell, not the tiny grip button.
            const cell = event.currentTarget.closest('.bento-cell')
            if (cell) {
              event.dataTransfer.setDragImage(cell, 20, 20)
            }
            setDragId(entry.id)
          }}
          onDragEnd={() => {
            setDragId(null)
            setDragOverId(null)
          }}
          aria-label={`Move ${widget.displayName}`}
          title={`Drag to reorder ${widget.displayName}`}
        >
          <IconGripVertical size={14} />
        </button>
        {isResizable(widget) && (
          <Button
            basic
            square
            className="bento-cell-size-btn"
            onClick={(event) => {
              event.stopPropagation()
              cycleEntrySize(entry.id, w, h)
            }}
            aria-label={`Resize ${widget.displayName}`}
            // Native title, not the Button `tooltip` prop: `tooltip` wraps the
            // button in an InteractiveTooltip span, which would break the
            // `.bento-cell > .balto-button` absolute-positioning selector.
            title={`Cycle size: next is ${nextSizeBucket(w, h, widget).label}`}
          >
            <IconArrowsMaximize size={14} />
          </Button>
        )}
        <Button
          basic
          square
          className="bento-cell-hide-btn"
          onClick={(event) => {
            // Stop propagation so the click doesn't reach any
            // widget-internal handler under the absolute overlay.
            event.stopPropagation()
            setEntryVisibility(entry.id, false)
          }}
          aria-label={`Hide ${widget.displayName}`}
          // Native title (see the resize button above for why not `tooltip`).
          title={`Hide ${widget.displayName}`}
        >
          <IconX size={14} />
        </Button>
        <widget.Component scope={activeTab} />
      </div>
    )
  }

  const resetActiveTab = () => {
    const fresh = buildSeedTab(activeTab)
    setState((prev) => ({
      ...prev,
      tabs: {
        ...prev.tabs,
        [prev.activeTab]: {
          ...prev.tabs[prev.activeTab],
          ...fresh,
        },
      },
    }))
  }

  const filterOrgId = useSelector((reduxState) => reduxState.qaCopilot.filters?.organizationId)
  const dashboardFilters = useSelector((reduxState) => reduxState.qaCopilot.filters)

  const activateTab = (tabKey) => {
    if (tabKey === activeTab) {
      return
    }
    // Filters survive tab switches; only the picker overlay varies per
    // tab. The overlay-sync effect below owns the refetch.
    setState((prev) => ({ ...prev, activeTab: tabKey }))
  }
  const setPickedEntity = useCallback((entity) => {
    // The picker doesn't mirror into filters.* -- the filter bar holds
    // the cohort and the picker holds the focused entity. The
    // overlay-sync effect below pushes the pick into redux + refetches.
    setState((prev) => ({
      ...prev,
      tabs: {
        ...prev.tabs,
        [prev.activeTab]: { ...prev.tabs[prev.activeTab], pickedEntity: entity },
      },
    }))
  }, [])

  // Single source of truth for the scope overlay: whenever (tab,
  // picked entity) changes -- tab clicks, picker picks, cohort-row
  // clicks, or a layout blob restored from the server -- push the
  // overlay into redux (so EVERY fetch path carries it: Apply Filters,
  // org switch, day-click drills) and refetch once. This replaces the
  // per-call-site override threading, which leaked org-wide fetches on
  // every path that didn't know about the overlay, raced the
  // auto-pick's scoped fetch on tab switches, and never scoped the
  // fetch after a server-restored pick.
  const activePickedEntity = activeTabState?.pickedEntity || null
  const overlayMountRef = useRef(true)
  useEffect(() => {
    const filterKey = TAB_FILTER_KEY[activeTab] || null
    dispatch(setScopeOverlay(filterKey ? { filterKey, entity: activePickedEntity } : null))
    const isMount = overlayMountRef.current
    overlayMountRef.current = false
    // Scoped tab without a pick yet: the picker auto-picks within a
    // tick and this effect re-fires -- fetching now would just race it
    // with an unscoped request.
    if (filterKey && !activePickedEntity) {
      return
    }
    // Mount on Overview: the filter bar's init path owns the first
    // unscoped fetch; only a restored scoped pick warrants ours.
    if (isMount && !filterKey) {
      return
    }
    const orgId = filterOrgId || currentUserOrgId
    if (orgId) {
      dispatch(fetchCopilotDashboardData(orgId))
    }
  }, [activeTab, activePickedEntity, dispatch, filterOrgId, currentUserOrgId])

  // Active picker per scope tab. Focus widgets read via
  // PickedEntityValuesContext; cohort widgets keep using
  // filters.scorecards (etc) for the comparison set.
  const pickedEntityValues = useMemo(
    () => ({
      scorecard: state.tabs.scorecard?.pickedEntity || null,
      agent: state.tabs.agent?.pickedEntity || null,
      tag: state.tabs.tag?.pickedEntity || null,
    }),
    [
      state.tabs.scorecard?.pickedEntity,
      state.tabs.agent?.pickedEntity,
      state.tabs.tag?.pickedEntity,
    ]
  )

  const getFilterFormOptions = () => {
    const opts = ['name', 'isDefault', 'dateRange', 'isWin']
    if (!isEmpty(data.playbooks)) {
      opts.push('playbooks')
    }
    if (!isEmpty(data.scorecards)) {
      opts.push('scorecards', 'scoredStatus')
    }
    if (!isEmpty(data.agents)) {
      opts.push('agents')
    }
    if (!isEmpty(data.tags)) {
      opts.push('tags')
    }
    if (!isEmpty(data.voipCampaigns)) {
      opts.push('voipCampaigns')
    }
    if (!isEmpty(data.dispositions)) {
      opts.push('dispositionsHeader', 'dispositions')
    }
    return opts
  }

  const handleSelectSavedFilter = (value) => {
    dispatch(value ? setSavedFilterById(value) : clearSavedFilter())
  }

  // Single source of truth used by the section rail and the render
  // loop so they can't drift apart.
  const visibilityCtx = useMemo(() => ({ activeTab, inViewAs }), [activeTab, inViewAs])
  const visibleEntries = useMemo(
    () =>
      filterVisibleEntries(activeTabState.layout, (id) => WIDGET_BY_ID[id], visibilityCtx).filter(
        // The KPI band renders as a fixed header, not a grid cell.
        (entry) => entry.id !== OVERVIEW_WIDGET_ID
      ),
    [activeTabState.layout, visibilityCtx]
  )

  const hiddenWidgetsForActive = useMemo(() => {
    const sectionNameByKey = Object.fromEntries(
      SECTIONS.map((section) => [section.key, section.name])
    )
    const eligibleIds = new Set(widgetsForTab(activeTab).map((widget) => widget.id))
    return (activeTabState.layout || [])
      .filter(
        (entry) =>
          entry.visible === false &&
          eligibleIds.has(entry.id) &&
          // The KPI band is a fixed header now, not a restorable widget.
          entry.id !== OVERVIEW_WIDGET_ID
      )
      .map((entry) => {
        const widget = WIDGET_BY_ID[entry.id]
        return {
          id: entry.id,
          displayName: widget?.displayName || entry.id,
          section: widget?.section,
          sectionName: sectionNameByKey[widget?.section] || 'Other',
        }
      })
  }, [activeTabState.layout, activeTab])

  // View-as mode hides the agent-comparison widgets (an agent
  // shouldn't see their peers ranked). Previously enforced by dropping
  // the whole "By agent" section from the section render; with the
  // flat grid it's an entry filter.
  const orderedEntries = useMemo(
    () =>
      inViewAs
        ? visibleEntries.filter((entry) => WIDGET_BY_ID[entry.id]?.section !== 'by-agent')
        : visibleEntries,
    [visibleEntries, inViewAs]
  )

  const showOverviewBand = widgetsForTab(activeTab).some(
    (widget) => widget.id === OVERVIEW_WIDGET_ID
  )

  const renderTabPicker = () => {
    if (activeTab === 'scorecard') {
      return (
        <SectionScorecardPicker
          pickedEntity={activeTabState.pickedEntity}
          onPick={setPickedEntity}
        />
      )
    }
    if (activeTab === 'agent') {
      return (
        <SectionAgentPicker pickedEntity={activeTabState.pickedEntity} onPick={setPickedEntity} />
      )
    }
    if (activeTab === 'tag') {
      return (
        <SectionTagPicker pickedEntity={activeTabState.pickedEntity} onPick={setPickedEntity} />
      )
    }
    return null
  }

  const tabPicker = renderTabPicker()

  return (
    <EntityClickContext.Provider value={handleEntityClick}>
      <PickEntityContext.Provider value={setPickedEntity}>
        <PickedEntityValuesContext.Provider value={pickedEntityValues}>
          <BrowseCallsContext.Provider value={browseCallsContextValue}>
            <PageHeader
              title="Quality Dashboard"
              className="copilot-dashboard-header"
              actions={
                <div className="dashboard-actions">
                  {/* PDF export via the browser's native print dialog
                      (Kyle: "vibe it, let me know what trade-offs").
                      The trade-off: no in-page chart mutation, but no
                      new heavyweight dep either (html2canvas etc.).
                      Print stylesheet in customizableDashboard.scss
                      hides chrome + collapses charts to full-width. */}
                  <Button
                    onClick={handleDownloadPdf}
                    icon={<IconFileTypePdf />}
                    title="Save the dashboard as a PDF"
                  >
                    Export PDF
                  </Button>
                  {/* The header "Browse calls" button was folded into
                      the Scored Calls widget (Kyle: "that's weird at
                      the top, it should be on the charts"). The
                      widget-triggered browse drawers -- ShowCallsButton
                      inside other widgets, drill-into-tier links --
                      still go through BrowseCallsContext + the drawer
                      below, so all functionality is preserved. */}
                  <AddWidgetMenu
                    hiddenWidgets={hiddenWidgetsForActive}
                    onAdd={(widgetId) => setEntryVisibility(widgetId, true)}
                  />
                  <ResetTabConfirm onConfirm={resetActiveTab} />
                  {!childOrgSelected && (
                    <SavedFilters
                      filterType={COPILOT_FILTER_TYPE}
                      selectedSavedFilterId={selectedSavedFilterId}
                      handleSelectSavedFilter={handleSelectSavedFilter}
                      filterFormOptions={getFilterFormOptions()}
                    />
                  )}
                </div>
              }
            />

            <CopilotFilters />

            <ViewAsAgentBanner agent={viewAsAgent} onClear={clearViewAs} />

            <DashboardTabs activeTab={activeTab} onActivate={activateTab} />

            <NavTrail entries={trail} onJumpToIndex={handleTrailJump} />

            {tabPicker}

            {COHORT_BANNER_TABS.has(activeTab) && activeTabState.pickedEntity && (
              <Banner brand className="cohort-banner">
                <div>
                  <Pill brand small bold className="cohort-banner-label">
                    Comparing
                  </Pill>{' '}
                  <strong>
                    {activeTabState.pickedEntity.label || activeTabState.pickedEntity.value}
                  </strong>{' '}
                  vs. every other {ENTITY_NOUN_BY_TAB[activeTab]} in your filter window.
                </div>
                <div className="cohort-banner-context">
                  The top row shows where this {ENTITY_NOUN_BY_TAB[activeTab]} ranks. The row below
                  shows its absolute numbers.
                </div>
              </Banner>
            )}

            <div className="dashboard-shell">
              <div className="dashboard-shell-main">
                {/* At-a-glance KPI band: a fixed full-width header, not a
                    grid cell, so it can grow when its tiles wrap on
                    narrow viewports instead of clipping/overlapping. */}
                {showOverviewBand && <OverviewCardsRow scope={activeTab} />}
                {/* One flat grid: every remaining widget (comparative
                    cohort cards included) drags anywhere in the user's
                    layout order. */}
                <div className="bento-grid">
                  {orderedEntries.map((entry) => renderWidgetCell(entry))}
                </div>
              </div>
            </div>

            {browseCallsOpen && (
              <Drawer
                autoTrigger
                extraWide
                title={<h2>Browse scored calls</h2>}
                onClose={() => {
                  setBrowseOverrides(null)
                  popTrail('browse-calls')
                }}
              >
                <div className="entity-drawer-body">
                  <ScoredCallsBrowser
                    scopeFilters={{ ...(dashboardFilters || {}), ...browseOverrides }}
                    orgId={filterOrgId || currentUserOrgId}
                    initialTier={browseOverrides?.initialTier}
                  />
                </div>
              </Drawer>
            )}

            {/* Re-key on agent id so each open re-mounts the drawer with fresh state. */}
            {selectedAgent && (
              <AgentDetailDrawer
                key={selectedAgent.user_id}
                agent={selectedAgent}
                onEnterViewAs={enterViewAs}
                onClose={() => {
                  setSelectedAgent(null)
                  popTrail('agent')
                }}
              />
            )}

            {/* Re-key on (criterion id, agentContext user_id) so switching
          between criteria OR between agent-scoped drilldowns (Failure
          Streak rows) re-mounts the drawer with fresh state. */}
            {selectedCriterion && (
              <CriterionDetailDrawer
                key={`${selectedCriterion.id || selectedCriterion.criteria_id}-${selectedCriterion.agentContext?.user_id || 'all'}`}
                criterion={selectedCriterion}
                onClose={() => {
                  setSelectedCriterion(null)
                  popTrail('criterion')
                }}
              />
            )}

            {/* Re-key on scorecard sid so switching between scorecards re-mounts. */}
            {selectedScorecard && (
              <ScorecardDetailDrawer
                key={selectedScorecard.scorecard_config_sid || selectedScorecard.sid}
                scorecard={selectedScorecard}
                onClose={() => {
                  setSelectedScorecard(null)
                  popTrail('scorecard')
                }}
              />
            )}
          </BrowseCallsContext.Provider>
        </PickedEntityValuesContext.Provider>
      </PickEntityContext.Provider>
    </EntityClickContext.Provider>
  )
}

export default CustomizableDashboardPage
